import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import AddForm from "../components/AddForm";
import UpdateForm from "../components/UpdateForm";
import MetricView from "../components/MetricView";

// RasPm Dashboard examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { RequestService } from "services/RequestService";

function PowerStationsDataTables() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedStation, setSelectedStation] = useState(null);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalItems: 0
  });

  const [loading, setLoading] = useState(true); 

  const [tableData, setTableData] = useState({
    columns: [
      {
        Header: "#",
        accessor: "index",
        width: "5%",
        sortable: true,
        align: "center",
      },
      {
        Header: "اسم المحطة",
        accessor: "name",
        width: "45%",
        sortable: true,
      },
      {
        Header:" سعة المحطة",
        accessor: "capacity",
        width: "35%",
        sortable: true,
      },
      {
        Header: "الإجراءات",
        accessor: "actions",
        width: "15%",
        sortable: false,
        align: "center",
      }
    ],
    rows: [],
  });

  const [notification, setNotification] = useState({
    color: "info",
    icon: "notifications",
    title: "تنبيه",
    content: "",
    dateTime: "",
    open: false,
  });

  const closeNotification = () => setNotification({ ...notification, open: false });

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleOpenUpdateDialog = (station) => {
    setSelectedStation(station);
    setOpenUpdateDialog(true);
  };

  const handleCloseUpdateDialog = () => {
    setSelectedStation(null);
    setOpenUpdateDialog(false);
  };

  const handleOpenDeleteDialog = (station) => {
    setSelectedStation(station);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedStation(null);
    setOpenDeleteDialog(false);
    setIsDeleting(false);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await RequestService.delete(`PowerStation/${selectedStation.id}`);
      if (response.data && !response.data.error) {
        fetchPowerStations();
        handleCloseDeleteDialog();
        setNotification({
          ...notification,
          color: "success",
          icon: "check",
          title: "لوحة ادارة ربان السفينة",
          content: response.data?.message || "تم حذف المحطة بنجاح",
          dateTime: new Date().toLocaleString('ar-IQ'),
          open: true,
        });
      } else {
        setNotification({
          ...notification,
          color: "error",
          icon: "error",
          title: "لوحة ادارة ربان السفينة",
          content: response.data?.message || "حدث خطأ أثناء حذف المحطة",
          dateTime: new Date().toLocaleString('ar-IQ'),
          open: true,
        });
      }
    } catch (error) {
      console.error("Error deleting power station:", error);
      setNotification({
        ...notification,
        color: "error",
        icon: "error",
        title: "لوحة ادارة ربان السفينة",
        content: error.response?.data?.message || "حدث خطأ أثناء حذف المحطة",
        dateTime: new Date().toLocaleString('ar-IQ'),
        open: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddSuccess = (response) => {
    if (response?.data) {
      fetchPowerStations();
      setOpenAddDialog(false);
      setNotification({
        ...notification,
        color: "success",
        icon: "check",
        title: "لوحة ادارة ربان السفينة",
        content: response.data?.message || "تم إضافة المحطة بنجاح",
        dateTime: new Date().toLocaleString('ar-IQ'),
        open: true,
      });
    } else {
      setNotification({
        ...notification,
        color: "error",
        icon: "error",
        title: "لوحة ادارة ربان السفينة",
        content: response.data?.message || "حدث خطأ أثناء إضافة المحطة",
        dateTime: new Date().toLocaleString('ar-IQ'),
        open: true,
      });
    }
  };

  const handleUpdateSuccess = (response) => {
    if (response?.data) {
      fetchPowerStations();
      handleCloseUpdateDialog();
      setNotification({
        ...notification,
        color: "success",
        icon: "check",
        title: "لوحة ادارة ربان السفينة",
        content: response.data?.message || "تم تحديث المحطة بنجاح",
        dateTime: new Date().toLocaleString('ar-IQ'),
        open: true,
      });
    } else {
      setNotification({
        ...notification,
        color: "error",
        icon: "error",
        title: "لوحة ادارة ربان السفينة",
        content: response.data?.message || "حدث خطأ أثناء تحديث المحطة",
        dateTime: new Date().toLocaleString('ar-IQ'),
        open: true,
      });
    }
  };

  const fetchPowerStations = async () => {
    try {
      setLoading(true);
      const skip = (pagination.page - 1) * pagination.pageSize;
      const take = pagination.pageSize;

      const response = await RequestService.get(`PowerStation?skip=${skip}&take=${take}`);
      if (response.data && !response.data.error) {
        const stations = response.data.data || [];
        setTableData({
          ...tableData,
          rows: stations.map((station, index) => ({
            index: index + 1,
            name: station.name,
            capacity: station.capacity,
            actions: (
              <MDBox display="flex" alignItems="center" gap={1}>
             
                <MDButton
                  variant="gradient"
                  color="info"
                  size="small"
                  onClick={() => handleOpenUpdateDialog(station)}
                  startIcon={<EditIcon />}
                >
                  تعديل
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => handleOpenDeleteDialog(station)}
                  startIcon={<DeleteIcon />}
                >
                  حذف
                </MDButton>
              </MDBox>
            ),
          })),
        });
        setPagination({
          ...pagination,
          totalItems: response.data.totalCount
        });
      }
    } catch (error) {
      console.error("Error fetching power stations:", error);
      setNotification({
        ...notification,
        color: "error",
        icon: "error",
        title: "لوحة ادارة ربان السفينة",
        content: "حدث خطأ أثناء جلب بيانات المحطات",
        dateTime: new Date().toLocaleString('ar-IQ'),
        open: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handlePageSizeChange = (newSize) => {
    setPagination({ page: 1, pageSize: newSize, totalItems: pagination.totalItems });
  };


  useEffect(() => {
    fetchPowerStations();
  }, [pagination.page, pagination.pageSize]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
                sx={{
                  backgroundColor: "grey.100",
                  borderBottom: "1px solid",
                  borderColor: "grey.300",
                }}
              >
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium">
                    قائمة المحطات
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    إدارة المحطات الكهربائية
                  </MDTypography>
                </MDBox>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleOpenAddDialog}
                  startIcon={<AddIcon />}
                >
                  إضافة محطة
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={tableData}
                  isSorted={true}
                  entriesPerPage={{
                    defaultValue: pagination.pageSize,
                    entries: [5, 10, 15, 20, 25],
                    onChange: handlePageSizeChange
                  }}
                  showTotalEntries={true}
                  canSearch={true}
                  noEndBorder
                  loading={loading}
                  pagination={{
                    variant: "gradient",
                    color: "info",
                    page: pagination.page,
                    totalPages: Math.ceil(pagination.totalItems / pagination.pageSize),
                    totalItems: pagination.totalItems,
                    onPageChange: handlePageChange
                  }}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <AddForm
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        onSuccess={handleAddSuccess}
      />
      <UpdateForm
        open={openUpdateDialog}
        onClose={handleCloseUpdateDialog}
        powerStation={selectedStation}
        onSuccess={handleUpdateSuccess}
      />
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        dir="rtl"
      >
        <DialogTitle>
          <MDTypography variant="h6">تأكيد الحذف</MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body1">
            هل أنت متأكد من حذف هذه المحطة؟
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ 
          padding: 2,
          display: 'flex',
          justifyContent: 'space-between',
          '& > button': {
            marginLeft: '8px',
            marginRight: '0px',
          }
        }}>
          <MDButton onClick={handleDelete} color="error" disabled={isDeleting}>
            {isDeleting ? "جاري الحذف..." : "حذف"}
          </MDButton>
          <MDButton onClick={handleCloseDeleteDialog} color="secondary">
            إلغاء
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        dateTime={notification.dateTime}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
      />
    </DashboardLayout>
  );
}

export default PowerStationsDataTables;
